import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { formatPrice, formatHour } from "@/lib/utils";
import { ChevronLeft, ChevronRight, Check, X, DollarSign, Ban, Flame } from "lucide-react";
import { toast } from "sonner";

const DAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

function pad(n) {
  return String(n).padStart(2, "0");
}

function monthDate(year, month, day) {
  return `${year}-${pad(month + 1)}-${pad(day)}`;
}

export default function BookingCalendar({ onRefresh }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [bookings, setBookings] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [courts, setCourts] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [blockCourtId, setBlockCourtId] = useState("");
  const [slots, setSlots] = useState([]);
  const [pickedBlockHours, setPickedBlockHours] = useState([]);
  const [loading, setLoading] = useState(true);

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startPad = firstDay.getDay();
  const daysInMonth = lastDay.getDate();

  const from = monthDate(year, month, 1);
  const to = monthDate(year, month, daysInMonth);

  useEffect(() => {
    setLoading(true);
    setSelectedDay(null);
    Promise.all([
      api.get(`/owner/bookings?from=${from}&to=${to}`),
      api.get(`/owner/blocked-dates?from=${from}&to=${to}`),
      api.get("/owner/courts"),
    ])
      .then(([bRes, blRes, cRes]) => {
        setBookings(bRes.data);
        setBlockedDates(blRes.data);
        setCourts(cRes.data.filter((c) => c.active));
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [from, to]);

  const prev = () => {
    if (month === 0) {
      setYear(year - 1);
      setMonth(11);
    } else {
      setMonth(month - 1);
    }
  };

  const next = () => {
    if (month === 11) {
      setYear(year + 1);
      setMonth(0);
    } else {
      setMonth(month + 1);
    }
  };

  const bookingsByDay = {};
  for (const b of bookings) {
    if (!bookingsByDay[b.date]) bookingsByDay[b.date] = [];
    bookingsByDay[b.date].push(b);
  }

  const blockedByDay = {};
  for (const bl of blockedDates) {
    if (!blockedByDay[bl.date]) blockedByDay[bl.date] = [];
    blockedByDay[bl.date].push(bl);
  }

  const reloadData = async () => {
    const [bRes, blRes] = await Promise.all([
      api.get(`/owner/bookings?from=${from}&to=${to}`),
      api.get(`/owner/blocked-dates?from=${from}&to=${to}`),
    ]);
    setBookings(bRes.data);
    setBlockedDates(blRes.data);
    if (onRefresh) onRefresh();
  };

  const selectedDateStr = selectedDay ? monthDate(year, month, selectedDay) : null;
  const dayBookings = selectedDateStr ? bookingsByDay[selectedDateStr] || [] : [];
  const dayBlocked = selectedDateStr ? blockedByDay[selectedDateStr] || [] : [];

  useEffect(() => {
    if (blockCourtId && selectedDateStr) {
      api.get(`/courts/${blockCourtId}/availability?date=${selectedDateStr}`)
        .then((r) => setSlots(r.data.slots || []))
        .catch(() => setSlots([]));
    } else {
      setSlots([]);
    }
    setPickedBlockHours([]);
  }, [blockCourtId, selectedDateStr]);

  const todayStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;

  const monthLabel = new Intl.DateTimeFormat(i18n.language?.startsWith("ar") ? "ar-JO" : "en-US", {
    year: "numeric",
    month: "long",
  }).format(firstDay);

  const dayLabels = DAYS.map((d) => t(`calendar.${d}`));

  const isRtl = i18n.language?.startsWith("ar");
  const PrevIcon = isRtl ? ChevronRight : ChevronLeft;
  const NextIcon = isRtl ? ChevronLeft : ChevronRight;

  const blockDay = async () => {
    if (!blockCourtId || !selectedDateStr || pickedBlockHours.length === 0) return;
    try {
      const sorted = [...pickedBlockHours].sort((a, b) => a - b);
      const step = slots.length > 1 ? slots[1].hour - slots[0].hour : 1;
      await api.post("/owner/blocked-dates", {
        court_id: blockCourtId,
        date: selectedDateStr,
        start_hour: sorted[0],
        end_hour: sorted[sorted.length - 1] + step,
      });
      toast.success(t("calendar.block_success"));
      setBlockCourtId("");
      setPickedBlockHours([]);
      setSlots([]);
      await reloadData();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const unblockDay = async (blockedId) => {
    try {
      await api.delete(`/owner/blocked-dates/${blockedId}`);
      toast.success(t("calendar.unblocked"));
      await reloadData();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const monthBookings = bookings;
  const monthRevenue = monthBookings.reduce((s, b) => s + (b.total_price || 0), 0);

  return (
    <div>
      {/* Month navigation */}
      <div className="flex items-center justify-between mb-6">
        <button onClick={prev} className="btn-outline rounded-sm px-3 py-2 text-xs flex items-center gap-1">
          <PrevIcon className="h-3.5 w-3.5" />
        </button>
        <span className="font-heading text-2xl tracking-tight">{monthLabel}</span>
        <button onClick={next} className="btn-outline rounded-sm px-3 py-2 text-xs flex items-center gap-1">
          <NextIcon className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="border border-border/30 bg-card p-3 text-center">
          <div className="font-heading text-2xl text-volt">{monthBookings.length}</div>
          <div className="text-[10px] tracking-widest uppercase text-muted-foreground/80">{t("calendar.bookings", "Bookings")}</div>
        </div>
        <div className="border border-border/30 bg-card p-3 text-center">
          <div className="font-heading text-2xl text-volt">{monthRevenue} JOD</div>
          <div className="text-[10px] tracking-widest uppercase text-muted-foreground/80">{t("calendar.revenue", "Revenue")}</div>
        </div>
        <div className="border border-border/30 bg-card p-3 text-center">
          <div className="font-heading text-2xl text-volt">{Math.round((monthBookings.length / daysInMonth) * 100)}%</div>
          <div className="text-[10px] tracking-widest uppercase text-muted-foreground/80">{t("calendar.occupancy", "Occupancy")}</div>
        </div>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 mb-1">
        {dayLabels.map((d) => (
          <div key={d} className="text-center text-[10px] tracking-widest uppercase text-muted-foreground/80 font-bold py-2">
            {d}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-px bg-border/20">
        {Array.from({ length: startPad }).map((_, i) => (
          <div key={`pad-${i}`} className="bg-background min-h-[80px]" />
        ))}
        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
          const dateStr = monthDate(year, month, day);
          const dBookings = bookingsByDay[dateStr] || [];
          const dBlocked = blockedByDay[dateStr] || [];
          const isToday = dateStr === todayStr;
          const isSelected = selectedDay === day;
          const isFullyBlocked = dBlocked.length === courts.length && courts.length > 0;
          const confirmed = dBookings.filter((b) => b.status === "confirmed" || b.status === "pending").length;
          const total = dBookings.reduce((s, b) => s + b.total_price, 0);

          return (
            <button
              key={day}
              onClick={() => setSelectedDay(isSelected ? null : day)}
              className={`min-h-[80px] p-1.5 text-start transition-colors hover:bg-muted flex flex-col ${
                isFullyBlocked ? "bg-card/40" : "bg-card"
              } ${isSelected ? "ring-1 ring-volt" : ""}`}
            >
              <span className={`text-xs font-bold mb-auto ${isToday ? "text-volt" : "text-muted-foreground"}`}>
                {day}
              </span>
              {dBlocked.length > 0 && (
                <div className="mt-auto space-y-0.5">
                  <div className="text-[9px] tracking-widest uppercase text-red-400 font-bold flex items-center gap-1">
                    <Ban className="h-2.5 w-2.5" /> {dBlocked.length} {t("calendar.blocked")}
                  </div>
                </div>
              )}
              {dBookings.length > 0 && (
                <div className="mt-auto space-y-0.5">
                  <div className="text-[9px] tracking-widest uppercase text-volt font-bold">
                    {dBookings.length} {dBookings.length === 1 ? t("calendar.booking") : t("calendar.bookings")}
                  </div>
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                    dBookings.some((b) => b.status === 'pending') ? 'bg-yellow-400' : 'bg-emerald-400'
                  }`} />
                  {confirmed > 0 && (
                    <div className="text-[9px] tracking-widest uppercase text-muted-foreground/80">
                      {t("calendar.confirmed")}: {confirmed}
                    </div>
                  )}
                  {total > 0 && (
                    <div className="text-[9px] text-volt/80 font-mono">
                      {formatPrice(total, "JOD")}
                    </div>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Selected day detail */}
      {selectedDay && (
        <div className="mt-6 border border-border bg-card">
          <div className="p-4 border-b border-border/30 flex items-center justify-between">
            <span className="label-eyebrow">
              {t("calendar.bookings_for")} {selectedDateStr}
            </span>
            <span className="text-xs text-muted-foreground/80">
              {dayBookings.length} {dayBookings.length === 1 ? t("calendar.booking") : t("calendar.bookings")}
              {dayBlocked.length > 0 && ` · ${dayBlocked.length} ${t("calendar.blocked")}`}
            </span>
          </div>

          {/* Block/unblock section */}
          <div className="p-4 border-b border-border/30 bg-background/50">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <span className="text-[10px] tracking-widest uppercase text-muted-foreground/80 font-bold">{t("calendar.block_slots")}</span>
              <select
                value={blockCourtId}
                onChange={(e) => {
                  setBlockCourtId(e.target.value);
                  setPickedBlockHours([]);
                }}
                className="bg-card border border-border px-3 py-1.5 text-xs focus:border-volt focus:outline-none"
              >
                <option value="">{t("calendar.select_court")}</option>
                {courts.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <button
                onClick={blockDay}
                disabled={!blockCourtId || pickedBlockHours.length === 0}
                className="border border-red-400 text-red-400 px-3 py-1.5 text-[10px] tracking-widest uppercase font-bold hover:bg-red-400 hover:text-primary-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <Ban className="h-3 w-3 inline me-1" /> {t("calendar.block")}
              </button>
            </div>
            {blockCourtId && selectedDateStr && (
              <div>
                <label className="label-eyebrow block mb-2 text-[10px]">{t("calendar.pick_slots")}</label>
                <div className="grid grid-cols-5 sm:grid-cols-8 gap-1.5 max-h-48 overflow-auto">
                   {slots.map((s) => {
                     const picked = pickedBlockHours.includes(s.hour);
                     const isHourBlocked = !s.available && dayBlocked.some((bl) => {
                       if (bl.start_hour === null) return true;
                       if (bl.start_hour != null && bl.end_hour != null) return s.hour >= bl.start_hour && s.hour < bl.end_hour;
                       return s.hour === bl.start_hour;
                     });
                     return (
                       <button
                         key={s.hour}
                         type="button"
                         disabled={!s.available}
                         onClick={() => {
                           if (!s.available) return;
                           setPickedBlockHours((prev) =>
                             prev.includes(s.hour) ? prev.filter((h) => h !== s.hour) : [...prev, s.hour]
                           );
                         }}
                         className={`py-2 text-xs font-mono border transition-all relative ${
                           isHourBlocked
                             ? "border-red-400/30 text-red-400/50 line-through cursor-not-allowed"
                             : !s.available
                             ? "border-red-400/20 bg-[repeating-linear-gradient(45deg,transparent,transparent_3px,rgba(248,113,113,0.06)_3px,rgba(248,113,113,0.06)_6px)] text-muted-foreground/60 cursor-not-allowed"
                             : picked
                             ? "border-red-400 bg-red-400/10 text-red-400 font-bold"
                             : "border-border hover:border-foreground/40"
                         }`}
                       >
                         {formatHour(s.hour)}
                         {s.is_peak && (
                           <Flame className="absolute top-0.5 end-0.5 h-2 w-2 text-volt opacity-80" />
                         )}
                         {(!s.available || isHourBlocked) && (
                           <span className="absolute top-0.5 start-0.5 bg-red-400/80 text-white text-[7px] leading-none px-1 py-[1px] font-bold tracking-wider">
                             {t("court.booked")}
                           </span>
                         )}
                       </button>
                     );
                   })}
                 </div>
              </div>
            )}
            {dayBlocked.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {dayBlocked.map((bl) => {
                  const court = courts.find((c) => c.id === bl.court_id);
                  const label = bl.start_hour != null
                    ? `${court ? court.name : bl.court_id} ${formatHour(bl.start_hour)}–${formatHour(bl.end_hour)}`
                    : court ? court.name : bl.court_id;
                  return (
                    <div key={bl.id} className="flex items-center gap-2 border border-red-400/30 bg-red-400/5 px-3 py-1.5">
                      <span className="text-[10px] tracking-widest uppercase text-red-400 font-bold">
                        <Ban className="h-2.5 w-2.5 inline me-1" />
                        {label}
                      </span>
                      <button
                        onClick={() => unblockDay(bl.id)}
                        className="text-[10px] text-muted-foreground/80 hover:text-foreground ml-1"
                        title={t("calendar.unblock")}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Bookings list */}
          {dayBookings.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground/80 text-sm">{t("calendar.no_bookings")}</div>
          ) : (
            <div className="divide-y divide-white/5">
              {dayBookings.map((b) => {
                const payLabel = t(`bookings.payment.${b.payment_status}`, b.payment_status);
                return (
                  <div key={b.id} className="p-4 flex flex-wrap items-center gap-4 cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => navigate(`/bookings/${b.id}`)}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold">{b.court_name}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {b.user_name} · {b.user_email}
                      </div>
                    </div>
                    <div className="text-xs font-mono">
                      {formatHour(b.start_hour)} – {formatHour(b.end_hour)}
                    </div>
                    <div className="text-volt font-bold text-sm">{formatPrice(b.total_price, b.currency)}</div>
                    <div>
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${
                        b.status === "confirmed" ? "border-volt text-volt" :
                        b.status === "pending" ? "border-yellow-400 text-yellow-400" :
                        b.status === "cancelled" ? "border-border text-muted-foreground/80" :
                        "border-red-400 text-red-400"
                      }`}>
                        {b.status}
                      </span>
                    </div>
                    <div>
                      <span className={`text-[10px] uppercase tracking-widest ${
                        b.payment_status === "paid_at_court" ? "text-volt" :
                        b.payment_status === "pending_cod" || b.payment_status === "pending_online" ? "text-yellow-400" :
                        "text-emerald-400"
                      }`}>
                        {payLabel}
                      </span>
                    </div>
                    {b.payment_status === "pending_cod" && b.status !== "cancelled" && b.status !== "rejected" && (
                      <button
                        onClick={async () => {
                          try {
                            await api.patch(`/bookings/${b.id}/mark-paid`);
                            toast.success(t("common.booking_marked_paid"));
                            await reloadData();
                          } catch (e) {
                            toast.error(formatApiError(e.response?.data?.detail));
                          }
                        }}
                        className="border border-volt text-volt px-2 py-1 text-[10px] tracking-widest uppercase font-bold hover:bg-volt hover:text-primary-foreground transition-colors"
                      >
                        <DollarSign className="h-3 w-3 inline" /> {t("owner.mark_paid")}
                      </button>
                    )}
                    {b.status === "pending" && (
                      <div className="flex gap-1">
                        <button
                          onClick={async () => {
                            try {
                              await api.patch(`/bookings/${b.id}/decision`, null, { params: { decision: "confirmed" } });
                              toast.success(t("common.booking_approved"));
                              await reloadData();
                            } catch (e) {
                              toast.error(formatApiError(e.response?.data?.detail));
                            }
                          }}
                          className="bg-volt text-primary-foreground px-2 py-1 text-[10px] tracking-widest uppercase font-bold"
                        >
                          <Check className="h-3 w-3 inline" /> {t("owner.approve")}
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              await api.patch(`/bookings/${b.id}/decision`, null, { params: { decision: "rejected" } });
                              toast.success(t("common.booking_rejected"));
                              await reloadData();
                            } catch (e) {
                              toast.error(formatApiError(e.response?.data?.detail));
                            }
                          }}
                          className="border border-red-400 text-red-400 px-2 py-1 text-[10px] tracking-widest uppercase font-bold"
                        >
                          <X className="h-3 w-3 inline" /> {t("owner.reject")}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
